import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  analyzeSelfReflections,
  deleteMySelfReflection,
  getMySelfReflection,
  getSelfReflectionAssessmentProgress,
  getSelfReflectionSubject,
  listSelfReflectionAnalysisCandidates,
  listSelfReflectionSubjects,
  saveInstructorSelfReflection,
  saveMySelfReflection,
  setSelfReflectionSubjectExcluded,
} from '../../api/selfReflection';
import SelfReflectionForm from './SelfReflectionForm';
import SelfReflectionResults from './SelfReflectionResults';
import SelfReflectionTrainingRecommendations from './SelfReflectionTrainingRecommendations';
import { getSelfReflectionAssessmentStatus } from './selfReflectionModel';
import {
  WACHBEREITSCHAFT_LABELS as SHARED_WACHBEREITSCHAFT_LABELS,
  getWachbereitschaftGroupOrder,
  normalizeWachbereitschaftValue,
} from '../../config/wachbereitschaft';

const INFO_PARAGRAPHS = [
  <strong>Liebe Mitglieder der SRHT Düsseldorf,</strong>,
  <>wir als Euer Ausbilderteam möchten künftig das Instrument der <strong>Selbstreflexion</strong> in unsere Ausbildungsarbeit integrieren.</>,
  <>Wir sind der Auffassung, dass wir in den vergangenen Jahren eine positive Fehlerkultur aufgebaut und gelebt haben – immer mit dem gemeinsamen Ziel, voneinander zu lernen, uns weiterzuentwickeln und als Team besser zu werden.</>,
  <>Gleichzeitig beschäftigt uns die Frage, wie wir bei der inzwischen großen Anzahl an Höhenrettern noch besser im Blick behalten können, wo Eure individuellen Stärken liegen und in welchen Bereichen Ihr Euch möglicherweise mehr Training oder Sicherheit wünscht. Dadurch können Übungen gezielter auf Eure Bedürfnisse abgestimmt und vorhandene Trainingszeiten noch besser genutzt werden.</>,
  <strong>Wichtig ist uns dabei: Der Selbstreflexionsbogen dient ausschließlich der persönlichen und gemeinsamen Trainingsplanung. Er stellt keine Leistungsbewertung dar und fließt in keiner Form in eine dienstliche Beurteilung oder Bewertung ein.</strong>,
  <>Es liegt uns fern, persönliche Notizen über Eure Fähigkeiten oder Leistungen zu führen. Stattdessen möchten wir Euch mit dem Selbstreflexionsbogen die Möglichkeit geben, selbst einzuschätzen, in welchen Bereichen Ihr Euch sicher fühlt und wo Ihr für Euch persönlich noch Trainingsbedarf seht.</>,
  <>Der ausgefüllte Bogen kann im Rahmen unserer App bei der Vorbereitung und Planung von Übungen herangezogen werden. So können wir auch bei einer größeren Anzahl an Höhenrettern schnell und zuverlässig erkennen, welche Themen für einzelne Personen oder für die Gruppe besonders relevant sind.</>,
  <>Die <strong>Selbstreflexion bleibt ausschließlich innerhalb der Höhenrettung</strong> und kann von Euch jederzeit bearbeitet oder gelöscht werden. Die Ausbilder haben dabei <strong>keine Schreibrechte an Eurem persönlichen Selbstreflexionsbogen</strong>.</>,
  <>Ergänzend geben die Ausbilder eine eigene Einschätzung zu den jeweiligen Kategorien ab. In der Auswertung könnt Ihr Eure eigene Einschätzung direkt mit der Einschätzung der Ausbilder vergleichen. Dabei geht es ausdrücklich nicht darum, festzustellen, wer „richtig“ oder „falsch“ liegt. Vielmehr kann der Vergleich dabei helfen, unterschiedliche Wahrnehmungen sichtbar zu machen und daraus sinnvolle Trainingsschwerpunkte abzuleiten.</>,
  <>Für die Planung zukünftiger Übungen werden sowohl Eure <strong>Selbsteinschätzung als auch die Einschätzung des Ausbilderteams gleichberechtigt berücksichtigt</strong>. Auf diese Weise möchten wir sicherstellen, dass unsere Ausbildung nicht nur aus Sicht der Ausbilder, sondern auch aus Eurer persönlichen Wahrnehmung heraus möglichst bedarfsgerecht gestaltet wird.</>,
  <>Wir hoffen daher auf Eure Unterstützung und möchten Euch ausdrücklich dazu ermuntern, den Selbstreflexionsbogen offen und ehrlich auszufüllen. <strong>Es geht nicht darum, möglichst gut dazustehen, sondern darum, gemeinsam herauszufinden, wo wir als Einzelne und als Team noch besser werden können.</strong></>,
  <>Wie immer gilt: Wenn Ihr Fragen, Anregungen oder auch Bedenken zum Verfahren habt, kommt bitte jederzeit auf uns zu.</>,
  <strong>Euer Ausbilderteam</strong>,
];

function flattenCriteria(categories = []) {
  return categories.flatMap((category) => category.criteria.flatMap((criterion) => (
    criterion.type === 'group' ? criterion.criteria : [criterion]
  )));
}

function readInfoExpanded() {
  return window.localStorage.getItem('selfReflection.infoCollapsed') !== 'true';
}

const SUBJECT_COLLATOR = new Intl.Collator('de-DE', { sensitivity: 'base', numeric: true });
const WACHBEREITSCHAFT_LABELS = {
  ...SHARED_WACHBEREITSCHAFT_LABELS,
  '1_wb': '1. Wachabteilung',
  '2_wb': '2. Wachabteilung',
};

function getWachbereitschaftLabel(value) {
  return WACHBEREITSCHAFT_LABELS[normalizeWachbereitschaftValue(value)] || 'Keine Wachabteilung';
}

function getWachbereitschaftOrder(value) {
  return getWachbereitschaftGroupOrder(value);
}

function formatAssessmentDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(date);
}

const ASSESSOR_STATUS_GROUPS = [
  { status: 'complete', label: 'Vollständig beurteilt', color: 'success' },
  { status: 'incomplete', label: 'Teilweise beurteilt', color: 'warning' },
  { status: 'none', label: 'Noch nicht begonnen', color: 'default' },
];

export default function SelbstreflexionPage() {
  const [tab, setTab] = useState('self');
  const [infoExpanded, setInfoExpanded] = useState(readInfoExpanded);
  const [data, setData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectCapabilities, setSubjectCapabilities] = useState({});
  const [subjectId, setSubjectId] = useState('');
  const [subjectData, setSubjectData] = useState(null);
  const [subjectSort, setSubjectSort] = useState('name');
  const [instructorDialogOpen, setInstructorDialogOpen] = useState(false);
  const [instructorLoading, setInstructorLoading] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [exclusionBusyId, setExclusionBusyId] = useState(null);
  const [analysisIds, setAnalysisIds] = useState([]);
  const [analysisDraftIds, setAnalysisDraftIds] = useState([]);
  const [analysisCandidates, setAnalysisCandidates] = useState([]);
  const [analysisCandidatesLoading, setAnalysisCandidatesLoading] = useState(false);
  const [analysisCandidatesRefreshKey, setAnalysisCandidatesRefreshKey] = useState(0);
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [mode, setMode] = useState('self');
  const [selfFormOpen, setSelfFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState('');

  const loadMine = async () => {
    const response = await getMySelfReflection();
    setData(response.data);
    return response.data;
  };

  useEffect(() => {
    loadMine().catch((loadError) => setError(loadError.response?.data?.error || 'Selbstreflexion konnte nicht geladen werden.'));
  }, []);

  const canAssess = Boolean(data?.capabilities?.canAssess);
  const canAnalyze = Boolean(data?.capabilities?.canAnalyze);
  const analysisMinimumParticipants = Number(data?.capabilities?.analysisMinimumParticipants) || 1;

  useEffect(() => {
    if ((tab === 'instructor' && !canAssess) || (tab === 'analysis' && !canAnalyze)) setTab('self');
  }, [canAnalyze, canAssess, tab]);

  useEffect(() => {
    if (!canAssess) return;
    setSubjectsLoading(true);
    listSelfReflectionSubjects().then((response) => {
      setSubjects(response.data?.subjects || []);
      setSubjectCapabilities(response.data?.capabilities || {});
    }).catch((loadError) => {
      setSubjects([]);
      setError(loadError.response?.data?.error || 'Höhenretter konnten nicht geladen werden.');
    }).finally(() => setSubjectsLoading(false));
  }, [canAssess]);

  useEffect(() => {
    if (!canAnalyze) {
      setAnalysisCandidates([]);
      return;
    }
    setAnalysisCandidatesLoading(true);
    listSelfReflectionAnalysisCandidates().then((response) => {
      const selfId = Number(data?.subject?.id);
      setAnalysisCandidates((response.data?.candidates || [])
        .map((candidate) => ({
          ...candidate,
          id: Number(candidate.id),
          isSelf: Number(candidate.id) === selfId,
        }))
        .sort((left, right) => SUBJECT_COLLATOR.compare(left.fullName || '', right.fullName || '')));
    }).catch((loadError) => {
      setAnalysisCandidates([]);
      setError(loadError.response?.data?.error || 'Teilnehmer konnten nicht geladen werden.');
    }).finally(() => setAnalysisCandidatesLoading(false));
  }, [analysisCandidatesRefreshKey, canAnalyze, data?.subject?.id]);

  const currentValues = data?.analysis?.self?.values || {};
  const criteriaByKey = useMemo(() => new Map(flattenCriteria(data?.config?.categories).map((criterion) => [criterion.key, criterion])), [data?.config?.categories]);
  const sortedSubjects = useMemo(() => [...subjects].sort((left, right) => {
    if (subjectSort === 'wachbereitschaft') {
      const watchComparison = getWachbereitschaftOrder(left.wachbereitschaft) - getWachbereitschaftOrder(right.wachbereitschaft);
      if (watchComparison !== 0) return watchComparison;
    }
    return SUBJECT_COLLATOR.compare(left.fullName || '', right.fullName || '');
  }), [subjectSort, subjects]);
  const openInstructorAssessment = async (subject) => {
    setSubjectId(String(subject.id));
    setSubjectData(null);
    setInstructorDialogOpen(true);
    setInstructorLoading(true);
    try {
      const response = await getSelfReflectionSubject(subject.id);
      setSubjectData(response.data);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Einschätzung konnte nicht geladen werden.');
      setInstructorDialogOpen(false);
    } finally {
      setInstructorLoading(false);
    }
  };

  const openAssessmentProgress = async (subject) => {
    if (!subjectCapabilities.canViewAssessmentDetails) return;
    setProgressData(null);
    setProgressDialogOpen(true);
    setProgressLoading(true);
    try {
      const response = await getSelfReflectionAssessmentProgress(subject.id);
      setProgressData(response.data);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Bewertungsfortschritt konnte nicht geladen werden.');
      setProgressDialogOpen(false);
    } finally {
      setProgressLoading(false);
    }
  };

  const toggleSubjectExclusion = async (subject) => {
    try {
      setExclusionBusyId(subject.id);
      setError('');
      await setSelfReflectionSubjectExcluded(subject.id, !subject.excluded);
      const response = await listSelfReflectionSubjects();
      setSubjects(response.data?.subjects || []);
      setSubjectCapabilities(response.data?.capabilities || {});
      setAnalysisIds([]);
      setAnalysisDraftIds([]);
      setAnalysisData(null);
      setAnalysisCandidatesRefreshKey((current) => current + 1);
      setSuccess(subject.excluded ? 'Benutzer wieder eingeblendet.' : 'Benutzer für Beurteilungen ausgeblendet.');
    } catch (updateError) {
      setError(updateError.response?.data?.error || 'Sichtbarkeit konnte nicht geändert werden.');
    } finally {
      setExclusionBusyId(null);
    }
  };

  const saveMine = async (values) => {
    try {
      setBusy(true); setError('');
      await saveMySelfReflection({ baseRevisionId: data?.currentRevisionId ?? null, values });
      await loadMine();
      setSuccess('Selbstreflexion gespeichert.');
      return true;
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Selbstreflexion konnte nicht gespeichert werden.');
      return false;
    } finally { setBusy(false); }
  };

  const saveInstructor = async (values) => {
    try {
      setBusy(true); setError('');
      await saveInstructorSelfReflection(subjectId, { baseRevisionId: subjectData?.ownAssessment?.revisionId ?? null, values });
      const [subjectResponse, subjectsResponse] = await Promise.all([
        getSelfReflectionSubject(subjectId),
        listSelfReflectionSubjects(),
      ]);
      setSubjectData(subjectResponse.data);
      setSubjects(subjectsResponse.data?.subjects || []);
      setSubjectCapabilities(subjectsResponse.data?.capabilities || {});
      setSuccess('Einschätzung gespeichert.');
      return true;
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Einschätzung konnte nicht gespeichert werden.');
      return false;
    } finally { setBusy(false); }
  };

  const openAnalysisDialog = () => {
    setAnalysisDraftIds(analysisIds);
    setAnalysisDialogOpen(true);
  };

  const toggleAnalysisCandidate = (candidateId) => {
    setAnalysisDraftIds((current) => (
      current.includes(candidateId)
        ? current.filter((id) => id !== candidateId)
        : [...current, candidateId]
    ));
  };

  const clearAnalysisSelection = () => {
    setAnalysisIds([]);
    setAnalysisDraftIds([]);
    setAnalysisData(null);
    setError('');
  };

  const runAnalysis = async () => {
    try {
      setBusy(true); setError('');
      const response = await analyzeSelfReflections(analysisDraftIds, 'combined');
      setAnalysisIds(analysisDraftIds);
      setAnalysisData(response.data);
      setAnalysisDialogOpen(false);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Auswertung konnte nicht geladen werden.');
    } finally { setBusy(false); }
  };

  if (!data) return <Box sx={{ p: 3 }}><LinearProgress />{error && <Alert severity="error">{error}</Alert>}</Box>;

  return (
    <Box sx={{ px: { xs: 1, sm: 3 }, py: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Selbstreflexion</Typography>
        <Accordion
          expanded={infoExpanded}
          onChange={(_, expanded) => { setInfoExpanded(expanded); window.localStorage.setItem('selfReflection.infoCollapsed', expanded ? 'false' : 'true'); }}
          sx={{ borderRadius: '8px !important', overflow: 'hidden', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontWeight: 700 }}>Information zur Selbstreflexion</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1.5}>{INFO_PARAGRAPHS.map((paragraph, index) => <Typography key={index} variant="body2">{paragraph}</Typography>)}</Stack></AccordionDetails>
        </Accordion>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}
        <Accordion defaultExpanded sx={{ borderRadius: '8px !important', overflow: 'hidden', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontWeight: 700 }}>Punktematrix</Typography></AccordionSummary>
          <AccordionDetails>
            <Stack spacing={0.75}>
              {(data?.config?.scoreOptions || []).map((option) => (
                <Typography key={option.value} variant="body2" sx={{ fontWeight: 700 }}>
                  {option.value} {option.value === 1 ? 'Punkt' : 'Punkte'} = {option.label}
                </Typography>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: { xs: 1, sm: 2 },
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { minHeight: 48 },
              '& .Mui-selected': { fontWeight: 700 },
            }}
          >
            <Tab value="self" label="Meine Selbstreflexion" />
            {canAssess && <Tab value="instructor" label="Einschätzung abgeben" />}
            {canAnalyze && <Tab value="analysis" label="Auswertung" />}
          </Tabs>

          <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {tab === 'self' && (
              <Stack spacing={3}>
            <SelfReflectionResults
              title="Einschätzungen"
              config={data.config}
              analysis={data.analysis}
              eligibleAssessorCount={data.eligibleAssessorCount}
              mode={mode}
              onModeChange={setMode}
              headerAction={(
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setSelfFormOpen(true)}>{data.currentRevisionId ? 'Neue Einschätzung abgeben' : 'Selbstreflexion ausfüllen'}</Button>
                  {data.currentRevisionId && <Button color="error" variant="outlined" onClick={() => setDeleteOpen(true)}>Selbstreflexionsbogen löschen</Button>}
                </Stack>
              )}
            />
            {data.history?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontWeight: 700 }}>Verlauf ({data.history.length})</Typography></AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.5}>
                    {data.history.map((revision, index) => {
                      const olderValues = data.history[index + 1]?.values || {};
                      const changes = Object.entries(revision.values || {}).filter(([key, value]) => olderValues[key] !== value);
                      return (
                        <Card variant="outlined" key={revision.id}>
                          <CardContent>
                            <Stack spacing={0.75}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{new Date(revision.createdAt).toLocaleString('de-DE')}</Typography>
                              {changes.map(([key, value]) => (
                                <Typography key={key} variant="body2">
                                  {criteriaByKey.get(key)?.label || key}: {olderValues[key] ?? '–'} → {value ?? 'Keine Aussage'}
                                </Typography>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
              </Stack>
            )}

            {tab === 'instructor' && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Höhenrettereinschätzung</Typography>
                  <ToggleButtonGroup exclusive size="small" value={subjectSort} onChange={(_, value) => value && setSubjectSort(value)}>
                    <ToggleButton value="name">Alphabetisch</ToggleButton>
                    <ToggleButton value="wachbereitschaft">Wachabteilung</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                {subjectsLoading && <LinearProgress />}
                {!subjectsLoading && <Stack divider={<Divider flexItem />} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  {sortedSubjects.map((subject) => {
                    const status = getSelfReflectionAssessmentStatus(subject);
                    const assessmentDate = formatAssessmentDate(subject.lastAssessedAt);
                    return (
                      <Box
                        key={subject.id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'minmax(0, 1fr) 40px 40px',
                            md: 'minmax(190px, 1fr) 140px 142px 52px 92px 40px 40px',
                          },
                          alignItems: 'center',
                          columnGap: 1,
                          rowGap: 0.75,
                          minHeight: 68,
                          px: { xs: 1.25, sm: 1.75 },
                          py: 1.25,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{subject.fullName}</Typography>
                          <Typography variant="caption" color="text.secondary">{getWachbereitschaftLabel(subject.wachbereitschaft)}</Typography>
                        </Box>
                        <Box sx={{ gridColumn: { xs: '1 / -1', md: 2 }, gridRow: { xs: 2, md: 1 } }}>
                          <Chip size="small" color={status.color} label={status.label} sx={{ width: { md: '100%' } }} />
                        </Box>
                        <Box sx={{ gridColumn: { xs: '1 / -1', md: 3 }, gridRow: { xs: 3, md: 1 }, minHeight: { xs: assessmentDate ? 24 : 0, md: 24 } }}>
                          {assessmentDate && <Chip size="small" variant="outlined" label={`Zuletzt: ${assessmentDate}`} sx={{ width: { md: '100%' } }} />}
                        </Box>
                        <Box sx={{ gridColumn: { xs: '1 / -1', md: 4 }, gridRow: { xs: 4, md: 1 } }}>
                          <Tooltip title={subjectCapabilities.canViewAssessmentDetails ? 'Details zum Bewertungsfortschritt anzeigen' : 'Begonnene Beurteilungen'}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${subject.startedAssessmentCount}/${subject.eligibleAssessorCount}`}
                              clickable={Boolean(subjectCapabilities.canViewAssessmentDetails)}
                              onClick={subjectCapabilities.canViewAssessmentDetails ? () => openAssessmentProgress(subject) : undefined}
                              sx={{ width: { md: '100%' } }}
                            />
                          </Tooltip>
                        </Box>
                        <Box sx={{ gridColumn: { xs: '1 / -1', md: 5 }, gridRow: { xs: 5, md: 1 }, minHeight: { xs: subject.excluded ? 24 : 0, md: 24 } }}>
                          {subject.excluded && <Chip size="small" color="error" variant="outlined" label="Ausgeblendet" />}
                        </Box>
                        <Box sx={{ gridColumn: { xs: 2, md: 6 }, gridRow: 1, width: 40, height: 40 }}>
                          {!subject.excluded && (
                            <Tooltip title={`Einschätzung für ${subject.fullName} bearbeiten`}>
                              <IconButton aria-label={`Einschätzung für ${subject.fullName} bearbeiten`} color="primary" onClick={() => openInstructorAssessment(subject)}>
                                <EditOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Box sx={{ gridColumn: { xs: 3, md: 7 }, gridRow: 1, width: 40, height: 40 }}>
                          {subjectCapabilities.canManageSubjectExclusions && (
                            <Tooltip title={subject.excluded ? 'Benutzer wieder einblenden' : 'Benutzer nicht beurteilen'}>
                              <span>
                                <IconButton
                                  aria-label={subject.excluded ? `${subject.fullName} wieder einblenden` : `${subject.fullName} nicht beurteilen`}
                                  color={subject.excluded ? 'success' : 'default'}
                                  disabled={exclusionBusyId === subject.id}
                                  onClick={() => toggleSubjectExclusion(subject)}
                                >
                                  {subject.excluded ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  {!sortedSubjects.length && <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Keine Höhenretter verfügbar.</Typography>}
                </Stack>}
              </Stack>
            )}

            {tab === 'analysis' && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Button variant="contained" onClick={openAnalysisDialog}>Teilnehmer auswählen</Button>
                    {analysisIds.length > 0 && <Chip label={`${analysisIds.length} Teilnehmer ausgewählt`} variant="outlined" />}
                  </Stack>
                  {analysisData && <Button color="error" variant="outlined" onClick={clearAnalysisSelection}>Teilnehmerauswahl aufheben</Button>}
                </Stack>
                {analysisIds.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {analysisCandidates.filter((candidate) => analysisIds.includes(candidate.id)).map((candidate) => candidate.fullName).join(', ')}
                  </Typography>
                )}
                {analysisData?.privacyMode === 'recommendations_only' && (
                  <Box component="section" sx={{ p: { xs: 1.5, sm: 2 }, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Stack spacing={2}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>Trainingsempfehlung</Typography>
                      <SelfReflectionTrainingRecommendations recommendations={analysisData.trainingRecommendations} />
                    </Stack>
                  </Box>
                )}
                {analysisData?.privacyMode !== 'recommendations_only' && analysisData && (
                  <SelfReflectionResults
                    title="Trainingsempfehlung"
                    config={analysisData.config}
                    analysis={analysisData.group}
                    individuals={analysisData.individuals}
                    mode="combined"
                    fixedMode
                    groupView
                  />
                )}
              </Stack>
            )}
          </Box>
        </Box>
      </Stack>

      <Dialog open={analysisDialogOpen} disableEscapeKeyDown fullWidth maxWidth="sm">
        <DialogTitle>Teilnehmer auswählen</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
            {analysisMinimumParticipants > 1 && (
              <Alert severity="info">Für eine anonyme Auswertung müssen mindestens {analysisMinimumParticipants} Teilnehmer ausgewählt werden.</Alert>
            )}
            {analysisCandidatesLoading && <LinearProgress />}
            <List disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {analysisCandidates.map((candidate, index) => {
              const selected = analysisDraftIds.includes(candidate.id);
              return (
                <ListItemButton
                  key={candidate.id}
                  selected={selected}
                  divider={index < analysisCandidates.length - 1}
                  onClick={() => toggleAnalysisCandidate(candidate.id)}
                >
                  <ListItemIcon sx={{ minWidth: 42 }}>
                    <Checkbox edge="start" checked={selected} tabIndex={-1} disableRipple inputProps={{ 'aria-label': `${candidate.fullName} auswählen` }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${candidate.fullName}${candidate.isSelf ? ' (Ich)' : ''}`}
                    secondary={candidate.wachbereitschaft ? getWachbereitschaftLabel(candidate.wachbereitschaft) : null}
                  />
                </ListItemButton>
              );
            })}
            </List>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setAnalysisDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" disabled={analysisDraftIds.length < analysisMinimumParticipants || busy} onClick={runAnalysis}>Auswerten</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={selfFormOpen} disableEscapeKeyDown fullWidth maxWidth="lg">
        <DialogTitle>{data.currentRevisionId ? 'Neue Einschätzung abgeben' : 'Selbstreflexion ausfüllen'}</DialogTitle>
        <DialogContent dividers>
          <SelfReflectionForm
            config={data.config}
            initialValues={currentValues}
            previousValues={currentValues}
            saving={busy}
            onSave={saveMine}
            onSaved={() => setSelfFormOpen(false)}
            onCancel={() => setSelfFormOpen(false)}
            saveLabel="Speichern"
          />
        </DialogContent>
      </Dialog>

      <Dialog open={instructorDialogOpen} disableEscapeKeyDown fullWidth maxWidth="lg">
        <DialogTitle>{`Einschätzung abgeben${subjectData?.subject?.fullName ? ` – ${subjectData.subject.fullName}` : ''}`}</DialogTitle>
        <DialogContent dividers>
          {instructorLoading && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}
          {!instructorLoading && subjectData && (
            <SelfReflectionForm
              config={subjectData.config}
              initialValues={subjectData.ownAssessment.values}
              previousValues={subjectData.ownAssessment.values}
              allowNoStatement
              saving={busy}
              onSave={saveInstructor}
              onSaved={() => setInstructorDialogOpen(false)}
              onCancel={() => setInstructorDialogOpen(false)}
              saveLabel="Speichern"
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={progressDialogOpen} onClose={() => setProgressDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{`Bewertungsfortschritt${progressData?.subject?.fullName ? ` – ${progressData.subject.fullName}` : ''}`}</DialogTitle>
        <DialogContent dividers>
          {progressLoading && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}
          {!progressLoading && progressData && (
            <Stack spacing={2}>
              {ASSESSOR_STATUS_GROUPS.map((group) => {
                const assessors = progressData.assessors.filter((assessor) => assessor.status === group.status);
                return (
                  <Stack key={group.status} spacing={0.75}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{group.label} ({assessors.length})</Typography>
                    {assessors.map((assessor) => (
                      <Stack key={assessor.id} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={0.75} sx={{ py: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{assessor.fullName}</Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                          <Chip size="small" color={group.color} label={`${assessor.assessedCriteriaCount}/${assessor.totalCriteriaCount} Kategorien`} />
                          {assessor.lastAssessedAt && <Chip size="small" variant="outlined" label={formatAssessmentDate(assessor.lastAssessedAt)} />}
                        </Stack>
                      </Stack>
                    ))}
                    {!assessors.length && <Typography variant="caption" color="text.secondary">Keine Personen</Typography>}
                  </Stack>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setProgressDialogOpen(false)}>Schließen</Button></DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} disableEscapeKeyDown>
        <DialogTitle>Selbstreflexionsbogen löschen</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ pt: 1 }}><Alert severity="warning">Alle eigenen Revisionen werden gelöscht. Die Einschätzungen der Lehrhöhenretter bleiben erhalten.</Alert><TextField label="Zur Bestätigung LÖSCHEN eingeben" value={deleteText} onChange={(event) => setDeleteText(event.target.value)} /></Stack></DialogContent>
        <DialogActions><Button onClick={() => setDeleteOpen(false)}>Abbrechen</Button><Button color="error" disabled={deleteText !== 'LÖSCHEN' || busy} onClick={async () => { setBusy(true); await deleteMySelfReflection(deleteText); setDeleteOpen(false); setDeleteText(''); await loadMine(); setBusy(false); }}>Löschen</Button></DialogActions>
      </Dialog>
    </Box>
  );
}